import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TextField, Grid, Tabs, Tab, Box, FormControlLabel, Checkbox, Fade, Zoom, Chip } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { SCUIButton, SCUILoadingLinear } from '../../atomic/atoms';
import { SCUIContent } from '../../atomic/organisms';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { useMannequin } from './context/MannequinContext';
import { MannequinQuery } from '../../services/studio/StudioQuery';
import { MannequinCommand } from '../../services/studio/StudioCommand';
import { MannequinPhotoUpload } from './components/MannequinPhotoUpload';
import Swal from 'sweetalert2';
import './Mannequin.scss';

interface TabPanelProps {
    children?: React.ReactNode;
    value: number;
    index: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps): React.JSX.Element | null => {
    if (value !== index) return null;
    return <Box sx={{ py: 3 }}>{children}</Box>;
};

export const MannequinForm = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { id } = useParams<{ id: string }>();
    const { isLoading, setIsLoading } = useMannequin();

    const [tabValue, setTabValue] = useState(0);
    const [selectedPhotoFile, setSelectedPhotoFile] = useState<File | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        agencyName: '',
        passNo: '',
        chest: 0,
        waist: 0,
        hip: 0,
        length: 0,
        shoeSize: 0,
        weight: null as number | null,
        isAdult: true,
        active: true,
        isAi: false,
        photoUrl: '',
        tempPhotoId: '',
    });

    const [tempPhotoId] = useState(() => {
        // Generate a unique ID for new mannequin photos
        return `temp-${Date.now()}-${window.crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`;
    });

    const isEditMode = Boolean(id);

    useEffect(() => {
        if (isEditMode && id) {
            fetchMannequinData();
        }
    }, [id]);

    const fetchMannequinData = async () => {
        setIsLoading(true);
        try {
            const response = await MannequinQuery.GetMannequinById(Number.parseInt(id!));

            if (response?.data) {
                const data = response.data;

                setFormData({
                    name: data.name || '',
                    agencyName: data.agencyName || '',
                    passNo: data.passNo || '',
                    chest: data.chest || 0,
                    waist: data.waist || 0,
                    hip: data.hip || 0,
                    length: data.length || 0,
                    shoeSize: data.shoeSize || 0,
                    weight: data.weight,
                    isAdult: data.isAdult,
                    active: data.active,
                    isAi: data.isAi || false,
                    photoUrl: data.photoUrl || '',
                    tempPhotoId: '',
                });

                // Tab'ı mankenin türüne göre ayarla: isAdult true ise tab 0 (Yetişkin), false ise tab 1 (Çocuk)
                const selectedTab = data.isAdult ? 0 : 1;
                setTabValue(selectedTab);
            }
        } catch {
            Swal.fire({
                icon: 'error',
                title: t('general.anErrorOccured'),
                text: t('mannequin.errorFetchingData'),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        setFormData((prev) => ({
            ...prev,
            isAdult: newValue === 0,
        }));
    };

    const handleInputChange = (field: string, value: any) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handlePhotoFileSelect = (file: File | null) => {
        setSelectedPhotoFile(file);
    };

    const handleSubmit = async () => {
        // Validation
        if (!formData.name.trim()) {
            Swal.fire({
                icon: 'warning',
                title: t('general.warning'),
                text: t('mannequin.nameRequired'),
            });
            return;
        }

        setIsLoading(true);
        try {
            const requestData = {
                ...formData,
                tempPhotoId: !isEditMode ? tempPhotoId : undefined,
                id: isEditMode ? Number.parseInt(id!) : undefined,
            };

            // BFF will handle photo upload
            if (isEditMode) {
                await MannequinCommand.PutMannequin(requestData, selectedPhotoFile);
            } else {
                await MannequinCommand.PostMannequin(requestData, selectedPhotoFile);
            }

            Swal.fire({
                icon: 'success',
                title: t('general.success'),
                text: isEditMode ? t('mannequin.updateSuccess') : t('mannequin.createSuccess'),
                timer: 1500,
                showConfirmButton: false,
            });

            history.push('/studio/mannequin');
        } catch {
            Swal.fire({
                icon: 'error',
                title: t('general.anErrorOccured'),
                text: isEditMode ? t('mannequin.updateError') : t('mannequin.createError'),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        history.push('/studio/mannequin');
    };

    return (
        <SCUIContent>
            <div className="mannequin-form-container">
                <SCUIBreadCrumb
                    title={t('mannequin.title')}
                    componentLink={'/studio/mannequin'}
                    components={[
                        {
                            title: isEditMode ? t('mannequin.editTitle') : t('mannequin.addNew'),
                            link: undefined,
                        },
                    ]}
                />

                <SCUILoadingLinear display={isLoading} />

                <Fade in={true} timeout={500}>
                    <Box className="mannequin-header" sx={{ mb: 3 }}>
                        <Box className="mannequin-header-title">
                            <AccountCircleIcon sx={{ fontSize: '32px', color: '#1976d2' }} />
                            <h1>{isEditMode ? t('mannequin.editTitle') : t('mannequin.addNew')}</h1>
                            {isEditMode && <Chip label={`ID: ${id}`} size="small" color="primary" />}
                        </Box>
                    </Box>
                </Fade>

                <Zoom in={true} timeout={600}>
                    <Box className="mannequin-form-paper">
                        {!isEditMode ? (
                            <Tabs
                                value={tabValue}
                                onChange={handleTabChange}
                                aria-label="mannequin tabs"
                                sx={{
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    mb: 3,
                                    '& .MuiTab-root': {
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                    },
                                    '& .Mui-selected': {
                                        color: '#1976d2 !important',
                                    },
                                }}
                            >
                                <Tab label={`👔 ${t('mannequin.adultMannequin')}`} />
                                <Tab label={`👶 ${t('mannequin.childMannequin')}`} />
                            </Tabs>
                        ) : (
                            <Box
                                sx={{
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    mb: 3,
                                    pb: 2,
                                }}
                            >
                                <Chip
                                    label={
                                        formData.isAdult
                                            ? `👔 ${t('mannequin.adultMannequin')}`
                                            : `👶 ${t('mannequin.childMannequin')}`
                                    }
                                    color="primary"
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        height: '40px',
                                        borderRadius: '8px',
                                    }}
                                />
                            </Box>
                        )}

                        <TabPanel value={tabValue} index={0}>
                            <Fade in={true} timeout={500}>
                                <Box className="mannequin-form-section">
                                    <div className="mannequin-form-section-title">📋 {t('mannequin.basicInfo')}</div>
                                    <Grid container spacing={3}>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 6,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label={t('mannequin.name')}
                                                value={formData.name}
                                                onChange={(e) => handleInputChange('name', e.target.value)}
                                                required
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 6,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label={t('mannequin.passNo')}
                                                value={formData.passNo}
                                                onChange={(e) => handleInputChange('passNo', e.target.value)}
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 6,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label={t('mannequin.agencyName')}
                                                value={formData.agencyName}
                                                onChange={(e) => handleInputChange('agencyName', e.target.value)}
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 6,
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData.active}
                                                        onChange={(e) => handleInputChange('active', e.target.checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label={t('mannequin.active')}
                                            />
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 6,
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData.isAi}
                                                        onChange={(e) => handleInputChange('isAi', e.target.checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label={t('mannequin.isAi')}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Fade>

                            <Fade in={true} timeout={700}>
                                <Box className="mannequin-form-section">
                                    <div className="mannequin-form-section-title">📏 {t('mannequin.measurements')}</div>
                                    <Grid container spacing={4}>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 4,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label={`📐 ${t('mannequin.chest')}`}
                                                type="number"
                                                value={formData.chest}
                                                onChange={(e) =>
                                                    handleInputChange('chest', parseInt(e.target.value) || 0)
                                                }
                                                variant="outlined"
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 4,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label={`📐 ${t('mannequin.waist')}`}
                                                type="number"
                                                value={formData.waist}
                                                onChange={(e) =>
                                                    handleInputChange('waist', parseInt(e.target.value) || 0)
                                                }
                                                variant="outlined"
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 4,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label={`📐 ${t('mannequin.hip')}`}
                                                type="number"
                                                value={formData.hip}
                                                onChange={(e) =>
                                                    handleInputChange('hip', parseInt(e.target.value) || 0)
                                                }
                                                variant="outlined"
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 4,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label={`📏 ${t('mannequin.length')} (cm)`}
                                                type="number"
                                                value={formData.length}
                                                onChange={(e) =>
                                                    handleInputChange('length', parseInt(e.target.value) || 0)
                                                }
                                                variant="outlined"
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 4,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label={`⚖️ ${t('mannequin.weight')} (kg)`}
                                                type="number"
                                                value={formData.weight || ''}
                                                onChange={(e) =>
                                                    handleInputChange(
                                                        'weight',
                                                        e.target.value ? parseInt(e.target.value) : null
                                                    )
                                                }
                                                variant="outlined"
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 4,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label={`👟 ${t('mannequin.shoeSize')}`}
                                                type="number"
                                                value={formData.shoeSize}
                                                onChange={(e) =>
                                                    handleInputChange('shoeSize', parseFloat(e.target.value) || 0)
                                                }
                                                variant="outlined"
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Fade>

                            <Fade in={true} timeout={900}>
                                <Box className="mannequin-form-section">
                                    <div className="mannequin-form-section-title">📷 {t('mannequin.photo')}</div>
                                    <MannequinPhotoUpload
                                        initialPhotoUrl={formData.photoUrl}
                                        onFileSelect={handlePhotoFileSelect}
                                    />
                                </Box>
                            </Fade>
                        </TabPanel>

                        <TabPanel value={tabValue} index={1}>
                            <Fade in={true} timeout={500}>
                                <Box className="mannequin-form-section">
                                    <div className="mannequin-form-section-title">📋 {t('mannequin.basicInfo')}</div>
                                    <Grid container spacing={3}>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 6,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label={t('mannequin.name')}
                                                value={formData.name}
                                                onChange={(e) => handleInputChange('name', e.target.value)}
                                                required
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 6,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label={t('mannequin.passNo')}
                                                value={formData.passNo}
                                                onChange={(e) => handleInputChange('passNo', e.target.value)}
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 6,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label={t('mannequin.agencyName')}
                                                value={formData.agencyName}
                                                onChange={(e) => handleInputChange('agencyName', e.target.value)}
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 6,
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData.active}
                                                        onChange={(e) => handleInputChange('active', e.target.checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label={t('mannequin.active')}
                                            />
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 6,
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData.isAi}
                                                        onChange={(e) => handleInputChange('isAi', e.target.checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label={t('mannequin.isAi')}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Fade>

                            <Fade in={true} timeout={700}>
                                <Box className="mannequin-form-section">
                                    <div className="mannequin-form-section-title">📏 {t('mannequin.physicalInfo')}</div>
                                    <Grid container spacing={4}>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 6,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label={`📏 ${t('mannequin.length')} (cm)`}
                                                type="number"
                                                value={formData.length}
                                                onChange={(e) =>
                                                    handleInputChange('length', parseInt(e.target.value) || 0)
                                                }
                                                variant="outlined"
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                md: 6,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                label={`⚖️ ${t('mannequin.weight')} (kg)`}
                                                type="number"
                                                value={formData.weight || ''}
                                                onChange={(e) =>
                                                    handleInputChange(
                                                        'weight',
                                                        e.target.value ? parseInt(e.target.value) : null
                                                    )
                                                }
                                                variant="outlined"
                                                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Fade>

                            <Fade in={true} timeout={900}>
                                <Box className="mannequin-form-section">
                                    <div className="mannequin-form-section-title">📷 {t('mannequin.photo')}</div>
                                    <MannequinPhotoUpload
                                        initialPhotoUrl={formData.photoUrl}
                                        onFileSelect={handlePhotoFileSelect}
                                    />
                                </Box>
                            </Fade>
                        </TabPanel>

                        <Fade in={true} timeout={1000}>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                <SCUIButton
                                    onClick={handleCancel}
                                    type="secondary"
                                    buttonType="button"
                                    sizeType="fixed"
                                    size="m"
                                    text={t('general.cancel')}
                                />
                                <SCUIButton
                                    onClick={handleSubmit}
                                    type="primary"
                                    buttonType="button"
                                    sizeType="fixed"
                                    size="m"
                                    text={t('general.save')}
                                />
                            </Box>
                        </Fade>
                    </Box>
                </Zoom>
            </div>
        </SCUIContent>
    );
};
